/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/table.css'
import './styles.scss'
import { TypescriptComponent } from 'components/typescriptExample'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Image, Link } from '@ta-interaktiv/contentbricks'
import { browserCheck } from '@ta-interaktiv/browsercheck'
import { TableSymptom } from 'components/tableSymptom'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'

// TODO Set the correct article ID here
const ARTICLE_ID = '425705756775'

const Quote = ({ children, author }) => {
  if (!children) return null
  return (
    <blockquote className='quote'>
      <p>{children}</p>
      {author && <span>{author}</span>}
    </blockquote>
  )
}

export function AppComponent() {
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className='ui vertical  very fitted  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>
              Heuschnupfen und Corona gleichzeitig: Was Pollen&shy;allergiker
              jetzt wissen müssen
            </h1>
            <h2>
              Wie unterscheidet man die Symptome? Schützen Masken auch vor
              Pollen? Ist die Impfung für Allergiker unbedenklich? Wir
              beantworten die wichtigsten Fragen in unserer Übersicht.
            </h2>
          </div>
        </div>
      </header>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>Yannick Wiget, Patrick Vögeli</div>
            <div className='ta updated'>Aktualisiert am 30. März 2021</div>
          </div>
          <div className='ta text'>
            <p>
              Der Hals kratzt, die Nase läuft. Man fühlt sich schlapp und muss
              ständig niesen. Ist das nur Heuschnupfen oder etwa doch eine
              Corona-Infektion? Diese Frage stellen sich momentan viele
              Schweizerinnen und Schweizer, denn mehr als 20 Prozent der
              Bevölkerung haben eine Pollenallergie – und die erste heftige
              Phase der Heuschnupfen-Saison steht kurz bevor. Wir zeigen, was
              Allergiker jetzt wissen müssen:
            </p>
            <h4 className='chapter'>
              <b>1</b>
              <br />
              Wann ist der Höhepunkt der Heuschnupfen-Saison?
            </h4>{' '}
            <p>
              Die Saison dauert fast das ganze Jahr. Aus der Vielzahl der in der
              Luft enthaltenen Pollen sind allerdings nur einige von Bedeutung:
              Hasel und Erle Anfang Jahr, Esche und Birke im Frühling, Gräser im
              Frühsommer und Sommer sowie Beifuss im Spätsommer. Diese Pflanzen
              sind für über 90 Prozent der Allergien verantwortlich. Die meisten
              Betroffenen reagieren nicht nur auf eine Pollenart, sondern auch
              auf solche von verwandten Pflanzen.
            </p>
            <h4 className='graphicTitle'>Wann die Pollen fliegen</h4>
            <div className='graphicSubtitle'>
              Messungen von Januar bis September, 2001-2020. Von Oktober bis
              Dezember ist die Pollenfalle ausser Betrieb.
            </div>
            <div className='graphicLegend'>
              <div className='itemWrapper'>
                <div className='square yellow'></div>
                <div className='legendItem'>schwach</div>
              </div>
              <div className='itemWrapper'>
                <div className='square orange'></div>
                <div className='legendItem'>mässig</div>
              </div>
              <div className='itemWrapper'>
                <div className='square red'></div>
                <div className='legendItem'>stark</div>
              </div>{' '}
              <div className='itemWrapper'>
                <div className='square purple'></div>
                <div className='legendItem'>sehr stark</div>
              </div>
            </div>
            <Image
              file={
                browserCheck.isMobile
                  ? require('../../images/pollenkalender-mobile.svg')
                  : require('../../images/pollenkalender.svg')
              }
              // file={require('../../images/pollenkalender.svg')}
              alt='pollenkalender'
              className='cbImage pollenkalender'
              source='Quelle: Meteo Schweiz'
            />
            <p>
              Im Tessin, wo es wärmer ist, fliegen die Pollen meist etwas früher
              als auf der Alpennordseite. Dieses Jahr blühte die Hasel in
              Locarno und Lugano schon um den 11. Januar. Im Mittelland war dies
              vielerorts erst Anfang Februar der Fall, wie die{' '}
              <Link href='https://www.pollenundallergie.ch/infos-zu-pollen-und-allergien/polleninformationen/pollendaten-pollenmessungen/?oid=1831&lang=de&pflanzenartId=1&messstationId=14&startDatum=2021-02-01#pageid-2137'>
                Pollenmessungen
              </Link>{' '}
              des Allergiezentrums Schweiz zeigen. Dieser Saisonstart liege etwa
              im Mittel, sagt Regula Gehrig, Phänologin bei Meteo Schweiz.
              «Wegen des warmen und trockenen Wetters im Februar gab es aber
              aussergewöhnlich viele Tage mit starker Haselpollenbelastung.»
            </p>
            <p>
              Die erste wirklich heftige Phase der Heuschnupfen-Saison steht
              unmittelbar bevor: Birkenpollen sorgen neben Gräsern am häufigsten
              für starke allergische Reaktionen. Je nach Standort beginnen sie
              Ende März oder Anfang April durch die Luft zu fliegen. Ein genaues
              Datum zu bestimmen, ist schwierig, weil der Pollenflug vom Wetter
              abhängig ist und sich auch von Jahr zu Jahr ändert. «Bäume haben
              teilweise einen Zweijahres-Rhythmus», erklärt Gehrig, «es ist gut
              möglich, dass diese Saison schwächer ausfällt als die letzte, die
              sehr stark war.»
            </p>
            <h4 className='chapter'>
              <b>2</b>
              <br />
              Beeinflusst der Pollenflug die Corona-Verbreitung?
            </h4>{' '}
            <p>
              Ja, bei einer hohen Pollenkonzentration in der Luft steigt die
              Gefahr einer Ansteckung mit Sars-CoV-2. Das hat kürzlich ein
              internationales Team unter Leitung der Technischen Universität
              München herausgefunden. Die 154 Forschenden analysierten für die{' '}
              <Link href='https://www.tum.de/die-tum/aktuelles/covid-19/artikel/article/36478/'>
                breit angelegte Studie
              </Link>{' '}
              im letzten Frühjahr Daten von 130 Messstationen in 31 Ländern auf
              fünf verschiedenen Kontinenten. Dort, wo der Pollenflug stark war,
              stiegen auch die Infektionszahlen.
            </p>
            <h4 className='graphicTitle'>
              Je höher die Pollenkonzentration, desto mehr Corona-Infektionen
            </h4>
            <div className='graphicSubtitle'>
              Abweichungen von der Norm (Anomalien) korrelierten im Frühjahr
              2020 in der Schweiz
            </div>
            <Image
              file={
                browserCheck.isMobile
                  ? require('../../images/pollenkonzentration-scatterplot-mobile.svg')
                  : require('../../images/pollenkonzentration-scatterplot.svg')
              }
              // file={require('../../images/pollenkalender.svg')}
              alt='Pollen Partikelgrössenvergleich'
              className='cbImage partikelgroessenvergleich'
              source='Quelle: Technische Universität München'
            />
            <p>
              Die Forscher berücksichtigten auch demografische Faktoren und
              Umweltbedingungen, darunter Temperatur, Luftfeuchtigkeit,
              Bevölkerungsdichte und die Wirkung von Lockdowns. In untersuchten
              Gebieten mit Lockdown-Regeln halbierte sich die Zahl der
              Corona-Ansteckungen im Schnitt bei vergleichbarer
              Pollenkonzentration in der Luft. Die täglichen Infektionsraten
              korrelierten aber überall mit der Pollenzahl.
            </p>
            <p>
              Die Erklärung laut den Forschern: Wenn Pollen fliegen, reagiert
              die Körperabwehr in abgeschwächter Form auf Viren der Atemwege.
              Der Körper produziert dann weniger sogenannter Interferone,
              Proteine mit antiviraler Wirkung. Das beeinflusst auch die
              Entzündungsreaktion und kann das Risiko von Atemwegserkrankungen
              erhöhen – unabhängig davon, ob Betroffene an Allergien gegenüber
              diesen Pollen leiden oder nicht.
            </p>
            <h4 className='chapter'>
              <b>3</b>
              <br />
              Wie unterscheidet man Heuschnupfen und Corona?
            </h4>{' '}
            <p>
              Die Symptome von Covid-19 und einer Pollenallergie ähneln sich
              teilweise. «Müdigkeit und ein allgemeines Unwohlsein können bei
              beiden auftreten und sind kein eindeutiges Anzeichen», sagt
              Sereina de Zordo vom Allergiezentrum Schweiz. Auch Schnupfen könne
              in beiden Fällen vorkommen, sei aber häufiger eine allergische
              Reaktion. Durch die laufende oder verstopfte Nase ist es auch
              möglich, dass man schlechter riecht. Ein plötzlicher Verlust des
              Geruchs- oder Geschmackssinns deutet jedoch klar auf Covid-19 hin.
            </p>
            <p>
              Bei einer Corona-Infektion ist zudem die Temperatur häufig erhöht.
              Eine Pollenallergie verursacht hingegen kein Fieber, auch wenn der
              Heuschnupfen auf Englisch «hay fever» heisst. Betroffene fühlen
              sich nicht wirklich krank. «Ein klassisches Symptom einer
              Pollenallergie ist dafür der Juckreiz im Gaumen, in den Ohren und
              der Nase», sagt de Zordo. «Es treten auch häufig Niesattacken auf.
              Die Augen sind gerötet und beissen. Das alles ist untypisch für
              Covid-19.»
            </p>
            <h4 className='graphicTitle'>Pollenallergie oder Covid-19?</h4>
            <div className='graphicSubtitle'>
              Diese Übersicht kann helfen, Symptome von Heuschnupfen und einer
              Corona-Infektion zu unterscheiden.
            </div>
            <TableSymptom />
            <p>
              Wichtig ist die Unterscheidung zwischen gewöhnlichem Heuschnupfen
              und allergischem Asthma. Dieses kann sich bei ungefähr einem
              Drittel der Betroffenen entwickeln, wenn die Pollenallergie nicht
              richtig behandelt wird. Dann weiten sich die Beschwerden von den
              oberen (Nasen-Rachen-Raum) auf die unteren Atemwege (Bronchien,
              Lungen) aus: ein sogenannter Etagenwechsel. Es kommt zu
              Hustenanfällen, einem Engegefühl in der Brust oder sogar zu
              Atemnot, wie sie auch bei Covid-19 häufig auftritt.
            </p>
            <p>
              Permanenter, trockener Reizhusten trete eigentlich nur bei
              allergischem Asthma auf, sagt de Zordo, auch wenn manche
              Heuschnupfen-Geplagte wegen des Kratzens im Hals ab und zu husten
              müssten. Im Unterschied zu Covid-19 seien die Symptome nicht
              anhaltend, sondern würden eher in Schüben kommen. «Wer aber
              unsicher ist, soll sich besser testen lassen und eine Ärztin oder
              einen Arzt konsultieren.»
            </p>
            <h4 className='chapter'>
              <b>4</b>
              <br />
              Gehören Allergiker zur Corona-Risikogruppe?
            </h4>{' '}
            <p>
              Wer Heuschnupfen oder saisonales Asthma hat, der gehört laut dem
              Bundesamt für Gesundheit (BAG) nicht zur Risikogruppe. Sereina de
              Zordo vom Allergiezentrum Schweiz teilt diese Einschätzung, weist
              aber auf schweres Asthma als Ausnahme hin. Nur ein kleiner Teil
              der Bevölkerung ist davon betroffen: Schätzungsweise 6000 Menschen
              leiden das ganze Jahr unter schwerem Asthma.
            </p>
            <p>
              Sie zählen laut dem{' '}
              <Link href='https://www.bag.admin.ch/bag/de/home/krankheiten/ausbrueche-epidemien-pandemien/aktuelle-ausbrueche-epidemien/novel-cov/krankheit-symptome-behandlung-ursprung/besonders-gefaehrdete-menschen.html'>
                BAG
              </Link>{' '}
              zur Risikogruppe, genauso wie ältere Menschen (weil die Gefahr für
              einen schweren Verlauf mit zunehmendem Alter steigt), schwangere
              Frauen und Erwachsene mit einer der folgenden Vorerkrankung:
            </p>
            <ul>
              <li>Bluthochdruck</li>
              <li>Diabetes</li>
              <li>Herz-Kreislauf-Erkrankungen</li>
              <li>Erkrankungen und Therapien, die das Immunsystem schwächen</li>
              <li>Krebs</li>
              <li>
                Schweres Übergewicht (Adipositas dritten Grades, BMI über 40)
              </li>
              <li>Leberzirrhose</li>
              <li>Chronische Nierenerkrankung</li>
              <li>
                Chronische Lungen- und Atemwegserkrankungen (z.B. schweres
                Asthma)
              </li>
            </ul>
            <p>
              Personen mit allergischem Asthma oder einfach gewöhnlichem
              Heuschnupfen sind nicht besonders gefährdet. Gemäss dem{' '}
              <Link href='https://www.aha.ch/allergiezentrum-schweiz/home/news/?oid=1450&lang=de&news_eintragId=1190'>
                Allergiezentrum
              </Link>{' '}
              ist kein Zusammenhang bekannt zwischen einer bestehenden Allergie
              und dem Auftreten oder Schweregrad einer Covid-Erkrankung. Zudem
              gibt es derzeit keine Hinweise, dass die Einnahme von Medikamenten
              gegen Allergien und Asthma (Antihistaminika, Kortisonpräparate)
              das Risiko erhöht.
            </p>
            <h4 className='chapter'>
              <b>5</b>
              <br />
              Schützen Masken auch vor Pollen?
            </h4>{' '}
            <p>
              Seit dem Ausbruch der Corona-Pandemie vor einem Jahr gehören
              Masken zum Alltag in der Schweiz. Sie schützen vor Sars-CoV-2,
              Grippeviren – aber auch vor Pollen. Das bestätigte kürzlich Arthur
              Helbling, Leiter der Allergologisch-Immunologischen Poliklinik am
              Inselspital Bern, gegenüber dem{' '}
              <Link href='https://www.pollenundallergie.ch/infos-zu-pollen-und-allergien/polleninformationen/pollenprognose/2130/?oid=3059&lang=de&news_eintragId=1308'>
                Allergiezentrum:
              </Link>{' '}
              «Masken sind selbst für die kleinsten Pollen nicht durchlässig.»
            </p>
            <p>
              Pollenkörner sind zwar nach Form und Oberflächenstruktur sehr
              vielgestaltig, aber in der Regel zwischen 10 und 100 Mikrometer
              gross und können so die Textilschichten von Masken nicht
              durchdringen. Die medizinische oder chirurgische Maske, die man
              auf der Strasse oft sieht (meist hellblau), filtert Partikel ab
              einem Durchmesser von 3 Mikrometern. Geprüfte Textilmasken, welche
              die{' '}
              <Link
                allowWordWrap
                href='https://sciencetaskforce.ch/wp-content/uploads/2020/11/201014_PB_Uebersicht_verschiedene_Maskentypen.pdf'
              >
                Anforderungen der Schweizer Covid-Taskforce
              </Link>{' '}
              erfüllen, halten Partikel bis zu 1 Mikrometer ab und
              Atemschutzmasken sogar solche mit einem Durchmesser von 0,3
              Mikrometern.
            </p>
            <h4 className='graphicTitle'>
              Masken filtern viel kleinere Partikel als Pollen
            </h4>
            <div className='graphicSubtitle'>
              Durchmesser der gefilterten Partikel in Mikrometern (μm) im
              Vergleich
            </div>
            <Image
              file={
                browserCheck.isMobile
                  ? require('../../images/pollen-groessenvergleich-mobile.svg')
                  : require('../../images/pollen-groessenvergleich.svg')
              }
              // file={require('../../images/pollenkalender.svg')}
              alt='Pollen Partikelgrössenvergleich'
              className='cbImage partikelgroessenvergleich'
              source='Quelle: Swiss National Covid-19 Science Task Force'
            />
            <p>
              Alle geprüften Masken fangen also Blütenpollen ab. «Diese
              erreichen die Schleimhäute nicht mehr. In den meisten Fällen
              werden dadurch Symptome wie eine laufende Nase, Juckreiz im Mund
              oder Niesen deutlich gemildert», sagt Helbling. «Masken zu tragen,
              kann das Virus und die Pollen gleichermassen von den Atemwegen
              fernhalten», bestätigt auch Claudia Traidl-Hoffmann, Professorin
              für Umweltmedizin an der{' '}
              <Link href='https://www.tum.de/die-tum/aktuelles/covid-19/artikel/article/36478/'>
                Technischen Universität München
              </Link>
              .
            </p>
            <p>
              Keine Hilfe sind Masken natürlich gegen juckende oder gerötete
              Augen. Hier helfen nur Tropfen zur Linderung. Auch das Niesen kann
              zum Problem werden. Denn wenn eine Maske feucht wird, bietet sie
              kaum mehr Schutz, weder vor Pollen noch vor Viren. Wer trotz Maske
              häufig niesen muss, sollte diese regelmässig wechseln, wenn nötig
              mehrmals am Tag.
            </p>
            <h4 className='chapter'>
              <b>6</b>
              <br />
              Ist die Covid-Impfung für Allergiker unbedenklich?
            </h4>{' '}
            <p>
              Ja, die Impfung ist laut dem{' '}
              <Link href='https://www.aha.ch/aktuelles?id=1056'>
                Allergiezentrum
              </Link>{' '}
              bei fast allen Allergien problemlos möglich – auch bei
              Heuschnupfen und saisonalem Asthma. Eine vorgängige Abklärung
              brauchen Personen, die mal mit schweren allergischen Symptomen,
              zum Beispiel einem Kollaps, auf einen unbekannten Auslöser
              reagiert haben. Ebenfalls Vorsicht geboten ist bei chronischem
              Nesselfieber, einer Mastozytose, einem Mastzellaktivierungssyndrom
              oder wenn die Serum-Werte des Botenstoffs Tryptase erhöht sind.
              Betroffene dieser seltenen Erkrankungen wissen aber in den meisten
              Fällen darüber Bescheid.
            </p>
            <p>
              Wie bei allen Medikamenten und Impfungen, beispielsweise gegen die
              Grippe, können auch bei den Covid-Impfungen Nebenwirkungen
              auftreten, wie die{' '}
              <Link href='https://www.swissmedic.ch/swissmedic/de/home/news/coronavirus-covid-19/covid-19-allergische-reaktionen-anaphylaxien.html'>
                Arzneimittelbehörde Swissmedic
              </Link>{' '}
              schreibt: beispielsweise Schmerzen an der Injektionsstelle, Kopf-
              und Muskelschmerzen oder Schüttelfrost. In ganz seltenen Fällen
              ist es mit den beiden Vakzinen, die auch in der Schweiz zum
              Einsatz kommen, bisher zu einer anaphylaktischen beziehungsweise
              starken allergischen Reaktion gekommen: Bei Pfizer/Biontech gab es
              1,1 solche Fälle pro 100'000 Impfungen, bei Moderna 0,25 Fälle pro
              100'000.
            </p>
            <Quote author='Arzneimittelbehörde Swissmedic'>
              «Heuschnupfen und Asthma stellen kein Hindernis für die Impfung
              dar.»
            </Quote>
            <p>
              Sollten auch in der Schweiz solche Einzelfälle auftreten, wären
              die Impfzentren dafür gut gerüstet: Adrenalin, Antihistaminika,
              Kortison und Sprays gegen Atemnot gehören zur Grundausrüstung.
              Zudem steht jede Person nach der Injektion während 15 Minuten
              unter Beobachtung, um schwere allergische Reaktionen, die
              praktisch immer sofort auftreten, gut behandeln zu können.
            </p>
            <p>
              Um Komplikationen zu verhindern, dürfen sich Personen mit einer
              bekannten Allergie auf einen Bestandteil eines Covid-Vakzins nicht
              impfen lassen. Heuschnupfen und Asthma würden aber keine
              Kontraindikation für die Impfung darstellen, macht Swissmedic
              ebenfalls deutlich.
            </p>
            {/* <div
              className=''
              style={{ marginBottom: '2em', marginTop: '2rem' }}
            >
              <AutofitIFrame src='//datawrapper.dwcdn.net/1uPtC/' />
            </div> */}
          </div>
        </div>
      </div>

      <Footer
        articleId={ARTICLE_ID}
        questions={[
          'Wie informativ war der Beitrag?',
          'Wie gefällt Ihnen die Gestaltung?'
        ]}
        credits={[
          ['Text', 'Yannick Wiget'],
          ['Digitale Umsetzung', 'Patrick Vögeli']
        ]}
      />
    </div>
  )
}
